import { useMemo, useState } from 'react';

import { Divider, type SelectChangeEvent } from '@mui/material';
import { isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useContainer } from 'unstated-next';

import { UsersRow } from './UsersRow';
import { StyledFormTitleContainer, StyledFormTitle } from '../../../../styles/input-row';
import { StyledPagination } from '../../../InviteStudents/styles/styled-components';
import AdminUsersContainer from '../../AdminUsersContainer';
import { FormContainer } from '../../components/AdminUsers/FileUploadForm';
import Footer from '../../components/AdminUsers/Footer';
import { blankUser } from '../../constants/admin-users';
import { type User } from '../../constants/user-roles';
import { findDuplicateEmailIndexes, findDuplicateExtUserIdIndexes, validateUsers } from '../../utils/userInputValidation';

const UsersForm: React.FC = () => {
    const { bulkUpsertUsers, bulkUpsertUsersStatus } = useContainer(AdminUsersContainer);
    const { t } = useTranslation(['admin_settings', 'generic']);
    const [currentPage, setCurrentPage] = useState(1);
    const [errorIndexes, setErrorIndexes] = useState<number[]>([]);
    const [users, setUsers] = useState<User[]>([{ ...blankUser }]);

    const perPage = 50;

    const numberOfUsers = users.length;
    const numberOfPages = Math.ceil(numberOfUsers / perPage);

    const addRow = () => {
        setUsers([...users, { ...blankUser }]);

        const newPageCreated = Math.ceil((numberOfUsers + 1) / perPage) !== currentPage;
        if (newPageCreated) setCurrentPage(currentPage + 1); // Advance the current page when a new page is created by clicking the "add" button
    };

    const removeRow = (index: number) => {
        const copyOfUsers = [...users];
        copyOfUsers.splice(index, 1);
        setUsers(copyOfUsers);
        setErrorIndexes(validateUsers(copyOfUsers));
    };

    const handleUserChange = (index: number, event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name: field, value } = event.target;

        setUsers(
            users.map((user: User, i) => {
                if (i === index) {
                    return { ...user, [field]: value };
                }

                return user;
            })
        );
    };

    const handleRoleChange = (index: number, event: SelectChangeEvent<string[]>) => {
        const { name, value } = event.target;

        setUsers(
            users.map((user: User, i) => {
                if (i === index) {
                    return { ...user, [name]: value };
                }

                return user;
            })
        );
    };

    const onSubmit = async () => {
        const usersToSubmit = users.filter((user) => !isEqual(user, blankUser));
        await bulkUpsertUsers(usersToSubmit);
        if (bulkUpsertUsersStatus === 'SUCCESS' || bulkUpsertUsersStatus === 'INITIAL') setUsers([{ ...blankUser }]);
    };

    const onCancel = () => {
        setUsers([{ ...blankUser }]);
    };

    const canSubmit = useMemo(() => {
        if (validateUsers(users).length !== 0) return false;

        return users.filter((user) => !isEqual(user, blankUser)).length > 0;
    }, [users]);

    const renderRows = () => {
        if (numberOfUsers === 0) {
            return (
                <UsersRow
                    key={0}
                    index={0}
                    user={blankUser}
                    addRow={addRow}
                    removeRow={removeRow}
                    handleUserChange={handleUserChange}
                    handleRoleChange={handleRoleChange}
                    lastRow={true}
                    isDuplicateEmail={false}
                    isDuplicateExtUserId={false}
                />
            );
        }
        // To reduce computational overhead with very large data we compose a set of paginated indexes from which to render the user rows
        const paginatedValidIndexes: number[] = [];
        let currentIndex = (currentPage - 1) * perPage;

        while (paginatedValidIndexes.length < perPage && currentIndex < numberOfUsers) {
            paginatedValidIndexes.push(currentIndex);
            currentIndex += 1;
        }

        return paginatedValidIndexes.map((userIndex) => {
            const lastRow = userIndex === numberOfUsers - 1;

            return (
                <UsersRow
                    key={userIndex}
                    index={userIndex}
                    user={users[userIndex]}
                    addRow={addRow}
                    removeRow={removeRow}
                    handleUserChange={handleUserChange}
                    handleRoleChange={handleRoleChange}
                    lastRow={lastRow}
                    hasErrors={errorIndexes.includes(userIndex)}
                    isDuplicateEmail={findDuplicateEmailIndexes(users).includes(userIndex)}
                    isDuplicateExtUserId={findDuplicateExtUserIdIndexes(users).includes(userIndex)}
                />
            );
        });
    };

    const renderPaginator = () => {
        if (numberOfPages > 1) {
            return (
                <StyledPagination
                    count={numberOfPages}
                    page={currentPage}
                    color="primary"
                    onChange={(event, page) => {
                        if (page !== null && page !== undefined) setCurrentPage(page);
                    }}
                />
            );
        }

        return null;
    };

    return (
        <FormContainer>
            <StyledFormTitleContainer>
                <StyledFormTitle>{t('generic:Form')}</StyledFormTitle>
                <Divider sx={{ margin: '13px 0px' }} />
            </StyledFormTitleContainer>
            {renderRows()}
            {renderPaginator()}
            <Footer
                onSubmit={onSubmit}
                onCancel={onCancel}
                canSubmit={canSubmit}
                isLoading={bulkUpsertUsersStatus === 'LOADING'}
            />
        </FormContainer>
    );
};

export default UsersForm;
